// UTILS
import convertToyyyyMMddThhmm from "../../utils/conversions/dates/yyyyMMddThhmm.date.conversion.util";
// HOOKS
import { createContext, useState, useEffect } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import useQuery from "../../hooks/other/useQuery.other.hook";
// GRAPHQL
import { GET_ORDERED_PARTS } from "../../graphql/queries/parts/orderedParts.queries";
import {
  UPDATE_ORDERED_PART_MUTATION,
  CREATE_ORDERED_PART_MUTATION,
  DELETE_MANY_ORDERED_PARTS_MUTATION,
} from "../../graphql/mutations/parts/orderedParts.part.mutation";
// CONSTANTS
import ORDERED_PART_STATUSES from "../../constants/parts/orderedPartStatuses.part.constant";

const DEFAULT_QUERY = {
  page: 1,
  limit: 25,
  sort: "-ordered_date",
  search: "",
};

const DEFAULT_STATUSES = ORDERED_PART_STATUSES.filter(
  (status) => status.checked
).map((status) => status.value);

const findOrderedPart = (orderedParts, id) => {
  return orderedParts.find((part) => part._id === id);
};

export const OrderedPartsContext = createContext({
  orderedParts: [],
  metaData: {},
  isLoading: false,
  query: {},
  orderedPartInput: {}, // Used for graphql query
  setOrderedPartInput: () => {},
  setMetaData: () => {},
  setIsLoading: () => {},
  setQuery: () => {},
  setOrderedParts: () => {},
  fetchOrderedParts: () => {},
  updateOrderedPart: () => {},
  deleteOrderedParts: () => {},
  createOrderedPart: () => {},
  handleLimitChange: () => {},
  handlePageChange: () => {},
  handleSearch: () => {},
  handleSort: () => {},
  handleChange: () => {},
  handleMultiSelectChange: () => {},
});

export const OrderedPartsProvider = ({ children }) => {
  const {
    query,
    setQuery,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
  } = useQuery({ defaultQuery: DEFAULT_QUERY });
  // STATE
  const [orderedParts, setOrderedParts] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [orderedPartInput, setOrderedPartInput] = useState({
    status: DEFAULT_STATUSES,
  });
  const [timedUpdate, setTimedUpdate] = useState(null);
  // QUERIES
  const [getOrderedParts] = useLazyQuery(GET_ORDERED_PARTS, {
    fetchPolicy: "no-cache",
  });
  // MUTATIONS
  const [deleteManyOrderedPartsMutation] = useMutation(
    DELETE_MANY_ORDERED_PARTS_MUTATION,
    { fetchPolicy: "no-cache" }
  ); // [1
  const [updateOrderedPartMutation] = useMutation(UPDATE_ORDERED_PART_MUTATION);
  // EFFECTS
  // Set page to last page if current page is greater than total pages
  useEffect(() => {
    if (metaData.totalPages < query.page)
      setQuery({ ...query, page: metaData.totalPages });
  }, [metaData]);

  // Fetch orderedParts on query change
  useEffect(() => {
    fetchOrderedParts();
  }, [query, orderedPartInput]);
  // METHODS
  const fetchOrderedParts = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await getOrderedParts({
        variables: {
          query: query || {},
          orderedPart: orderedPartInput || {},
        },
      });
      if (data?.orderedParts?.orderedParts) {
        setOrderedParts(
          data?.orderedParts?.orderedParts.map((orderedPart) => {
            return {
              ...orderedPart,
              eta: convertToyyyyMMddThhmm(orderedPart?.eta),
              ordered_date: convertToyyyyMMddThhmm(orderedPart?.ordered_date),
              received_date: convertToyyyyMMddThhmm(orderedPart?.received_date),
              created_at: convertToyyyyMMddThhmm(orderedPart?.created_at),
              updated_at: convertToyyyyMMddThhmm(orderedPart?.updated_at),
            };
          })
        );
        setMetaData(data?.orderedParts?.metaData);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };
  // Updates a single orderedPart
  const updateOrderedPart = async (orderedPart) => {
    try {
      if (timedUpdate) clearTimeout(timedUpdate);
      const timeout = setTimeout(async () => {
        await updateOrderedPartMutation(
          {
            variables: {
              _id: orderedPart._id,
              orderedPart: {
                name: orderedPart.name,
                vendor_part_number: orderedPart.vendor_part_number,
                tickets: orderedPart.tickets?.map((t) => {
                  return {
                    ticket: t.ticket.toString(),
                    id: t.id,
                    ticket_id: t?.ticket_id,
                  };
                }),
                description: orderedPart.description,
                received_date: orderedPart.received_date,
                vendor: orderedPart.vendor,
                status: orderedPart.status,
                ordered_date: orderedPart.ordered_date,
                po_number: orderedPart.po_number,
                quantity: +orderedPart.quantity,
                each_price: +orderedPart.each_price,
                shipping_price: +orderedPart.shipping_price,
                total_price: +orderedPart.total_price,
                payment_method: orderedPart.payment_method,
                tracking_number: orderedPart.tracking_number,
                eta: orderedPart.eta,
                notes: orderedPart.notes,
              },
            },
          },
          { fetchPolicy: "no-cache" }
        );
      }, 1000);
      setTimedUpdate(timeout);
    } catch (error) {
      console.log(error);
      alert(`Error updating orderedPart: ${error.message}`);
    }
  };
  // Deletes many orderedParts
  const deleteOrderedParts = async (orderedParts) => {
    deleteManyOrderedPartsMutation({
      variables: {
        orderedParts: orderedParts.map((orderedPart) => ({
          _id: orderedPart._id,
        })),
      },
    });
    fetchOrderedParts();
  };
  // Create orderedPart
  const createOrderedPart = async (orderedPart) => {
    const { data, error } = await CREATE_ORDERED_PART_MUTATION(
      {
        variables: {
          orderedPart: {
            name: orderedPart.name,
            vendor_part_number: orderedPart.vendor_part_number,
            tickets: orderedPart.tickets.map((t) => {
              return {
                ticket: t.ticket.toString(),
                id: t.id,
                ticket_id: t?.ticket_id,
              };
            }),
            description: orderedPart.description,
            vendor: orderedPart.vendor,
            status: orderedPart.status,
            ordered_date: orderedPart.ordered_date,
            po_number: orderedPart.po_number,
            quantity: +orderedPart.quantity,
            each_price: +orderedPart.each_price,
            shipping_price: +orderedPart.shipping_price,
            total_price: +orderedPart.total_price,
            payment_method: orderedPart.payment_method,
            tracking_number: orderedPart.tracking_number,
            eta: orderedPart.eta,
            notes: orderedPart.notes,
          },
        },
      },
      { fetchPolicy: "network-only" }
    );
    return data.createOrderedPart.orderedPart;
  };
  const handleChange = async (event) => {
    const { name, value, dataset } = event.target;
    const { id } = dataset;
    const orderedPart = findOrderedPart(orderedParts, id);
    setOrderedParts(
      orderedParts.map((part) =>
        part._id === orderedPart._id ? { ...orderedPart, [name]: value } : part
      )
    );
    await updateOrderedPart({ ...orderedPart, [name]: value });
    if (name === "status") {
      setTimeout(fetchOrderedParts, 4000);
    }
  };

  const handleMultiSelectChange = (event) => {
    event.preventDefault();
    const { name, id, ticket, description, reset, ticket_id, parent_id } =
      event.target.dataset;
    const orderedPart = findOrderedPart(orderedParts, parent_id);
    const options = orderedPart[name];
    const getChange = () => {
      if (reset) return { ...orderedPart, [name]: [] };
      if (options?.find((value) => value.id === id)) {
        return {
          ...orderedPart,
          [name]: options.filter((value) => value.id !== id),
        };
      }
      return {
        ...orderedPart,
        [name]: [...options, { id: id, ticket, description, ticket_id }],
      };
    };
    const change = getChange();
    setOrderedParts(
      orderedParts.map((part) => (part._id === orderedPart._id ? change : part))
    );
    updateOrderedPart(change);
  };
  return (
    <OrderedPartsContext.Provider
      value={{
        orderedParts,
        metaData,
        isLoading,
        query,
        orderedPartInput,
        setOrderedPartInput,
        setMetaData,
        setIsLoading,
        setQuery,
        setOrderedParts,
        fetchOrderedParts,
        updateOrderedPart,
        deleteOrderedParts,
        createOrderedPart,
        handleLimitChange,
        handlePageChange,
        handleSearch,
        handleSort,
        handleChange,
        handleMultiSelectChange,
      }}
    >
      {children}
    </OrderedPartsContext.Provider>
  );
};
